import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import BlockIcon from "@mui/icons-material/Block";
import RefreshIcon from "@mui/icons-material/Refresh";
import ServiceRoleEditor from "./ServiceRoleEditor";

import { api } from "../../../../api/apiClient";
import { notify } from "../../../components-ui/toast";
import { QualificationTypeDto, RoleType, ServiceRoleDto, ServiceRoleQualificationRequirementDto } from "../../../types/types";

import "../../../../css/cards.css";
import "../../../../css/forms.css";
import "../../../../css/buttons.css";

interface ServiceRolesCardProps {
  serviceId: number;
  serviceName?: string;
}

const STANDARD_SERVICE_USER_ROLE_NAME = "Service User";

function isSystemServiceRole(role: ServiceRoleDto) {
  return (
    role.name.trim().toLowerCase() ===
    STANDARD_SERVICE_USER_ROLE_NAME.toLowerCase()
  );
}

export default function ServiceRolesCard({
  serviceId,
  serviceName,
}: ServiceRolesCardProps) {
  const [roles, setRoles] = useState<ServiceRoleDto[]>([]);
  const [roleTypes, setRoleTypes] = useState<RoleType[]>([]);
  const [qualificationTypes, setQualificationTypes] = useState<QualificationTypeDto[]>([]);
  const [roleRequirements, setRoleRequirements] = useState<ServiceRoleQualificationRequirementDto[]>([]);
  const [selectedSuggestedRoleTypeId, setSelectedSuggestedRoleTypeId] =
    useState<number | null>(null);
  const [editingRole, setEditingRole] = useState<ServiceRoleDto | null>(null);
  const [addingRole, setAddingRole] = useState(false);
  const [loadingRequirements, setLoadingRequirements] = useState(false);
  const [requirementsError, setRequirementsError] = useState("");
  const editRequest = useRef(0);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [rolesLoadError, setRolesLoadError] = useState("");

  const loadRoles = useCallback(async () => {
    try {
      setLoadingRoles(true);
      setRolesLoadError("");

      const result = await api<ServiceRoleDto[]>(
        `/api/services/${serviceId}/roles`,
        { suppressLoading: true, notifyError: false },
      );

      setRoles(result ?? []);
    } catch (error) {
      console.error("Failed to load service roles", error);
      setRolesLoadError("Unable to load the existing roles.");
    } finally {
      setLoadingRoles(false);
    }
  }, [serviceId]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  useEffect(() => {
    let cancelled = false;

    api<RoleType[]>("/api/static/roletypes", {
      suppressLoading: true,
      notifyError: false,
    })
      .then((result) => {
        if (!cancelled) setRoleTypes(result ?? []);
      })
      .catch((error) => {
        console.error("Failed to load suggested service roles", error);
        if (!cancelled) setRoleTypes([]);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    api<QualificationTypeDto[]>("/api/qualifications/types", { suppressLoading: true, notifyError: false })
      .then((result) => setQualificationTypes(result.filter((x) => x.isActive)))
      .catch((error) => console.error("Failed to load qualification types", error));
  }, []);

  const suggestedRoleTypes = useMemo(
    () =>
      roleTypes.filter(
        (roleType) =>
          roleType.roleName.trim().toLowerCase() !==
          STANDARD_SERVICE_USER_ROLE_NAME.toLowerCase(),
      ),
    [roleTypes],
  );
  const availableSuggestedRoleTypes = useMemo(
    () =>
      suggestedRoleTypes.filter((roleType) => {
        const matchingRole = roles.find(
          (role) =>
            role.name.trim().toLowerCase() ===
            roleType.roleName.trim().toLowerCase(),
        );
        return !matchingRole?.isActive;
      }),
    [roles, suggestedRoleTypes],
  );
  const selectedSuggestedRole = availableSuggestedRoleTypes.find(
    (roleType) => roleType.roleTypeId === selectedSuggestedRoleTypeId,
  );
  const selectedSuggestedExistingRole = selectedSuggestedRole
    ? roles.find(
        (role) =>
          role.name.trim().toLowerCase() ===
          selectedSuggestedRole.roleName.trim().toLowerCase(),
      )
    : undefined;

  const resetForm = () => {
    editRequest.current++;
    setAddingRole(false);
    setLoadingRequirements(false);
    setRequirementsError("");
    setEditingRole(null);
    setName("");
    setDescription("");
    setRoleRequirements([]);
  };

  const saveRole = async () => {
    if (!name.trim() || loadingRequirements || requirementsError) return;

    try {
      setSaving(true);
      if (editingRole) {
        await api<ServiceRoleDto>(`/api/services/roles/${editingRole.serviceRoleId}`, {
          method: "PUT",
          body: {
            name,
            description,
            isActive: editingRole.isActive,
          },
        });
        await api(`/api/qualifications/service-roles/${editingRole.serviceRoleId}/requirements`, {
          method: "PUT",
          body: { requirements: roleRequirements },
        });
        notify.success("Role updated.");
      } else {
        await api<ServiceRoleDto>(`/api/services/${serviceId}/roles`, {
          method: "POST",
          body: { name, description },
        });
        notify.success("Role added.");
      }

      resetForm();
      await loadRoles();
      setSelectedSuggestedRoleTypeId(null);
    } catch (error) {
      console.error("Failed to save service role", error);
      notify.error("Failed to save role.");
    } finally {
      setSaving(false);
    }
  };

  const deactivateRole = async (role: ServiceRoleDto) => {
    try {
      setSaving(true);
      await api<void>(`/api/services/roles/${role.serviceRoleId}`, {
        method: "DELETE",
      });
      notify.success("Role deactivated.");
      await loadRoles();
    } catch (error) {
      console.error("Failed to deactivate service role", error);
      notify.error("Failed to deactivate role.");
    } finally {
      setSaving(false);
    }
  };

  const addSuggestedRole = async (roleType: RoleType) => {
    const matchingRole = roles.find(
      (role) =>
        role.name.trim().toLowerCase() ===
        roleType.roleName.trim().toLowerCase(),
    );

    if (matchingRole?.isActive) return;

    try {
      setSaving(true);

      if (matchingRole) {
        await api<ServiceRoleDto>(
          `/api/services/roles/${matchingRole.serviceRoleId}`,
          {
            method: "PUT",
            body: {
              name: matchingRole.name,
              description: matchingRole.description ?? "",
              isActive: true,
            },
          },
        );
        notify.success(`${roleType.roleName} restored.`);
      } else {
        await api<ServiceRoleDto>(`/api/services/${serviceId}/roles`, {
          method: "POST",
          body: {
            name: roleType.roleName,
            description: "",
          },
        });
        notify.success(`${roleType.roleName} added.`);
      }

      await loadRoles();
      setSelectedSuggestedRoleTypeId(null);
    } catch (error) {
      console.error("Failed to add suggested service role", error);
      notify.error(`Failed to add ${roleType.roleName}.`);
    } finally {
      setSaving(false);
    }
  };

  const beginEditRole = async (role: ServiceRoleDto) => {
    const request = ++editRequest.current;
    setAddingRole(false);
    setEditingRole(role);
    setName(role.name);
    setDescription(role.description ?? "");
    setRoleRequirements([]);
    setRequirementsError("");
    setLoadingRequirements(true);
    try {
      const requirements = await api<ServiceRoleQualificationRequirementDto[]>(
          `/api/qualifications/service-roles/${role.serviceRoleId}/requirements`,
          { suppressLoading: true },
        );
      if (request === editRequest.current) setRoleRequirements(requirements);
    } catch (error) {
      console.error("Failed to load role requirements", error);
      if (request === editRequest.current) setRequirementsError("Unable to load qualification requirements. Close and reopen this role to try again.");
    } finally {
      if (request === editRequest.current) setLoadingRequirements(false);
    }
  };

  const toggleRequirement = (type: QualificationTypeDto) => {
    setRoleRequirements((current) =>
      current.some((item) => item.qualificationTypeId === type.qualificationTypeId)
        ? current.filter((item) => item.qualificationTypeId !== type.qualificationTypeId)
        : [...current, { qualificationTypeId: type.qualificationTypeId, qualificationName: type.name, requirementLevel: "Required" }],
    );
  };

  const renderEditor = () => (
    <ServiceRoleEditor name={name} description={description} onNameChange={setName}
      onDescriptionChange={setDescription} onSave={saveRole} onCancel={resetForm}
      saving={saving} disabled={loadingRequirements || Boolean(requirementsError)}
      saveLabel={editingRole ? "Save changes" : "Add role"}>
      {loadingRequirements && <p role="status">Loading qualification requirements...</p>}
      {requirementsError && <p className="field-error" role="alert">{requirementsError}</p>}
        {editingRole && !loadingRequirements && !requirementsError && qualificationTypes.length > 0 && (
          <div className="service-role-requirements">
            <div className="service-role-section-heading"><h3>Qualification requirements</h3><span className="service-role-count">{roleRequirements.length}</span></div>
            <p>Required qualifications warn rota managers when evidence is missing, expired or unverified.</p>
            <div className="service-role-requirement-list">
              {qualificationTypes.map((type) => {
                const requirement = roleRequirements.find((item) => item.qualificationTypeId === type.qualificationTypeId);
                return <div className="service-role-requirement" key={type.qualificationTypeId}>
                  <label><input type="checkbox" checked={Boolean(requirement)} onChange={() => toggleRequirement(type)} /> {type.name}</label>
                  {requirement && <select className="form-input" value={requirement.requirementLevel} onChange={(event) => setRoleRequirements((current) => current.map((item) => item.qualificationTypeId === type.qualificationTypeId ? { ...item, requirementLevel: event.target.value as "Required" | "Preferred" } : item))}><option>Required</option><option>Preferred</option></select>}
                </div>;
              })}
            </div>
          </div>
        )}

    </ServiceRoleEditor>
  );

  return (
    <div className="card service-roles-card">
      <div className="service-roles-card__header">
        <div>
          <div className="card-title">Manage roles</div>
          <p className="service-roles-card__intro">
            Create and maintain the roles available for{" "}
            {serviceName ?? "this service"}. Select a role name to edit it in place.
          </p>
        </div>
        <button type="button" className="link-button link-button-with-icon" aria-label="Add role"
          title="Add role" aria-expanded={addingRole} disabled={saving || loadingRoles}
          onClick={() => { resetForm(); setAddingRole(true); }}><AddIcon fontSize="small" /> Add role</button>
      </div>

      {addingRole && suggestedRoleTypes.length > 0 && (
        <section
          className="service-suggested-roles"
          aria-labelledby="suggested-service-roles-title"
        >
          <div className="service-role-section-heading">
            <div>
              <h3 id="suggested-service-roles-title">Suggested roles</h3>
              <p>
                Add a common role without entering it manually. Service User is
                included automatically.
              </p>
            </div>
          </div>

          <div className="service-suggested-role-list service-suggested-role-list--desktop">
            {suggestedRoleTypes.map((roleType) => {
              const matchingRole = roles.find(
                (role) =>
                  role.name.trim().toLowerCase() ===
                  roleType.roleName.trim().toLowerCase(),
              );
              const isConfigured = matchingRole?.isActive === true;

              return (
                <div
                  key={roleType.roleTypeId}
                  className={`service-suggested-role${
                    isConfigured ? " service-suggested-role--configured" : ""
                  }`}
                >
                  <span>{roleType.roleName}</span>
                  {isConfigured ? (
                    <span className="service-suggested-role-status">
                      Configured
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="action-button compact secondary"
                      onClick={() => addSuggestedRole(roleType)}
                      disabled={saving}
                    >
                      <AddIcon fontSize="small" />
                      {matchingRole ? "Restore" : "Add"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          <div className="service-suggested-role-mobile">
            {availableSuggestedRoleTypes.length > 0 ? (
              <div className="service-suggested-role-picker">
                <select
                  className="form-input"
                  value={selectedSuggestedRoleTypeId ?? ""}
                  onChange={(event) =>
                    setSelectedSuggestedRoleTypeId(
                      event.target.value ? Number(event.target.value) : null,
                    )
                  }
                  aria-label="Select a suggested role"
                  disabled={saving}
                >
                  <option value="">Select a role...</option>
                  {availableSuggestedRoleTypes.map((roleType) => {
                    const existingRole = roles.find(
                      (role) =>
                        role.name.trim().toLowerCase() ===
                        roleType.roleName.trim().toLowerCase(),
                    );
                    return (
                      <option
                        key={roleType.roleTypeId}
                        value={roleType.roleTypeId}
                      >
                        {roleType.roleName}
                        {existingRole ? " (inactive)" : ""}
                      </option>
                    );
                  })}
                </select>
                <button
                  type="button"
                  className="action-button service-suggested-role-add"
                  onClick={() =>
                    selectedSuggestedRole && addSuggestedRole(selectedSuggestedRole)
                  }
                  disabled={saving || !selectedSuggestedRole}
                >
                  <AddIcon fontSize="small" />
                  {selectedSuggestedExistingRole ? "Restore" : "Add"}
                </button>
              </div>
            ) : (
              <div className="service-suggested-role-complete">
                All suggested roles are configured.
              </div>
            )}
          </div>
        </section>
      )}

      {addingRole && <section className="service-role-editor"><h3>Add a role</h3>{renderEditor()}</section>}

      <section
        className="service-role-list-section"
        aria-labelledby="configured-roles-title"
      >
        <div className="service-role-list-heading">
          <h3 id="configured-roles-title">Configured roles</h3>
          <div className="service-role-list-heading__actions">
            <span className="service-role-count">
              {roles.length} {roles.length === 1 ? "role" : "roles"}
            </span>
            <button
              type="button"
              className="icon-button service-role-refresh"
              onClick={loadRoles}
              disabled={loadingRoles || saving}
              aria-label="Refresh roles"
              title="Refresh roles"
            >
              <RefreshIcon fontSize="small" />
            </button>
          </div>
        </div>

        <div className="service-role-list">
          {loadingRoles && (
            <div className="service-role-list-message" role="status">
              Loading existing roles...
            </div>
          )}

          {!loadingRoles && rolesLoadError && (
            <div className="service-role-list-message service-role-list-message--error" role="alert">
              <span>{rolesLoadError}</span>
              <button
                type="button"
                className="action-button compact secondary"
                onClick={loadRoles}
              >
                Try again
              </button>
            </div>
          )}

          {!loadingRoles && !rolesLoadError && roles.map((role) => (
            <div
              key={role.serviceRoleId}
              className={`service-role-item${
                role.isActive ? "" : " service-role-item--inactive"
              }`}
            >
              {editingRole?.serviceRoleId === role.serviceRoleId ? (
                <div className="service-role-inline-editor">{renderEditor()}</div>
              ) : <>
              <div className="service-role-item__content">
                <div className="service-role-item__heading">
                  {isSystemServiceRole(role) ? <div className="service-role-item__name">{role.name}</div> :
                    <button type="button" className="service-role-item__name service-role-name-button"
                      onClick={() => beginEditRole(role)} disabled={saving}
                      aria-label={`Edit ${role.name}`} title="Click to edit role">{role.name}</button>}
                  <span
                    className={`service-role-status service-role-status--${
                      role.isActive ? "active" : "inactive"
                    }`}
                  >
                    {role.isActive ? "Active" : "Inactive"}
                  </span>
                  {isSystemServiceRole(role) && (
                    <span className="service-role-status service-role-status--system">
                      System
                    </span>
                  )}
                </div>
                <p
                  className={`service-role-item__description${
                    role.description
                      ? ""
                      : " service-role-item__description--empty"
                  }`}
                >
                  {role.description || "No description provided."}
                </p>
              </div>
              <div className="service-role-item__actions">
                {!isSystemServiceRole(role) && (
                  <>
                    {role.isActive && (
                      <button
                        type="button"
                        className="icon-button service-role-action service-role-action--danger"
                        aria-label="Deactivate role"
                        title="Deactivate role"
                        onClick={() => deactivateRole(role)}
                        disabled={saving}
                      >
                        <BlockIcon fontSize="small" />
                      </button>
                    )}
                  </>
                )}
              </div>
              </>}
            </div>
          ))}

          {!loadingRoles && !rolesLoadError && roles.length === 0 && (
            <div className="service-role-empty-state">
              <strong>No roles configured</strong>
              <span>Use Add role above to create your first role.</span>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
